import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, ExternalLink } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GpsMapProps {
  latitude: number;
  longitude: number;
  title?: string;
  className?: string;
}

// Component to handle map center updates
function MapCenterHandler({ latitude, longitude }: { latitude: number; longitude: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView([latitude, longitude], 15);
  }, [map, latitude, longitude]);

  return null;
}

export function GpsMap({ latitude, longitude, title, className = '' }: GpsMapProps) {
  const position: [number, number] = [latitude, longitude];

  // Format coordinates for display
  const formatCoord = (coord: number, isLat: boolean) => {
    const direction = isLat ? (coord >= 0 ? 'N' : 'S') : (coord >= 0 ? 'E' : 'W');
    return `${Math.abs(coord).toFixed(6)}° ${direction}`;
  };

  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

  return (
    <div className={`rounded-xl overflow-hidden border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <MapPin className="w-5 h-5" />
          <span className="font-medium text-sm">Photo Location</span>
        </div>
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-white/90 hover:text-white text-xs bg-white/20 hover:bg-white/30 px-2.5 py-1.5 rounded-full transition-colors"
        >
          <span>Open in Maps</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Map */}
      <div className="h-48 sm:h-56">
        <MapContainer
          center={position}
          zoom={15}
          scrollWheelZoom={false}
          className="h-full w-full"
          style={{ zIndex: 0 }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position}>
            <Popup>
              <div className="text-center">
                {title && <p className="font-medium mb-1">{title}</p>}
                <p className="text-xs text-gray-600">
                  {formatCoord(latitude, true)}<br />
                  {formatCoord(longitude, false)}
                </p>
              </div>
            </Popup>
          </Marker>
          <MapCenterHandler latitude={latitude} longitude={longitude} />
        </MapContainer>
      </div>

      {/* Coordinates footer */}
      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
        <span className="font-mono">{formatCoord(latitude, true)}</span>
        <span className="font-mono">{formatCoord(longitude, false)}</span>
      </div>
    </div>
  );
}
